
import React, { useState, useEffect } from 'react';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import Select from '../../components/base/Select';
import Modal from '../../components/base/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface Kontakt {
  id: string;
  name: string;
  firma?: string;
  email?: string;
  telefon?: string;
  adresse?: string;
  plz?: string;
  ort?: string;
}

interface VertragsBedingung {
  id: string;
  kategorie: string;
  wert: string;
  beschreibung?: string;
}

interface VertragsData {
  vertragsNummer: string;
  vertragsTyp: string;
  kontaktPartei1: string;
  kontaktPartei2: string;
  vertragsDatum: string;
  startDatum: string;
  endDatum: string;
  betreff: string;
  beschreibung: string;
  bedingungen: VertragsBedingung[];
  zahlungskonditionen: string;
  kuendigungsklausel: string;
  firmenName: string;
  firmenAdresse: string;
  firmenTelefon: string;
  firmenEmail: string;
  rechtlicheHinweise: string;
  unterschriftOrt: string;
}

interface VertragsHistoryItem {
  id: string;
  user_id: string;
  contract_number: string;
  contract_type: string;
  party1_name: string;
  party2_name: string;
  subject: string;
  contract_date: string;
  start_date: string;
  end_date: string;
  status: 'entwurf' | 'versendet' | 'unterzeichnet' | 'aktiv' | 'beendet' | 'gekuendigt';
  is_sent: boolean;
  pdf_generated_count: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

const vertragsTypen = [
  { value: 'dienstleistung', label: 'Dienstleistungsvertrag' },
  { value: 'wartung', label: 'Wartungsvertrag' },
  { value: 'miet', label: 'Mietvertrag' },
  { value: 'kauf', label: 'Kaufvertrag' },
  { value: 'leasing', label: 'Leasingvertrag' },
  { value: 'lizenz', label: 'Lizenzvertrag' },
  { value: 'kooperation', label: 'Kooperationsvertrag' },
  { value: 'rahmen', label: 'Rahmenvertrag' },
  { value: 'sonstige', label: 'Sonstiger Vertrag' }
];

const standardBedingungen = {
  dienstleistung: [
    { kategorie: 'Leistungsumfang', wert: '', beschreibung: 'Detaillierte Beschreibung der zu erbringenden Leistungen' },
    { kategorie: 'Vergütung', wert: '', beschreibung: 'Höhe und Art der Vergütung' },
    { kategorie: 'Zahlungsweise', wert: 'Monatlich im Voraus', beschreibung: 'Zahlungsrhythmus und -modalitäten' },
    { kategorie: 'Laufzeit', wert: '12 Monate', beschreibung: 'Vertragslaufzeit' },
    { kategorie: 'Kündigung', wert: '3 Monate zum Monatsende', beschreibung: 'Kündigungsfristen und -modalitäten' }
  ],
  wartung: [
    { kategorie: 'Wartungsumfang', wert: '', beschreibung: 'Umfang der Wartungsarbeiten' },
    { kategorie: 'Wartungsintervall', wert: 'Monatlich', beschreibung: 'Häufigkeit der Wartungen' },
    { kategorie: 'Reaktionszeit', wert: '24 Stunden', beschreibung: 'Reaktionszeit bei Störungen' },
    { kategorie: 'Ersatzteile', wert: 'Inklusive', beschreibung: 'Regelung für Ersatzteile' },
    { kategorie: 'Vertragslaufzeit', wert: '24 Monate', beschreibung: 'Laufzeit des Wartungsvertrags' }
  ],
  miet: [
    { kategorie: 'Mietobjekt', wert: '', beschreibung: 'Genaue Beschreibung des Mietobjekts' },
    { kategorie: 'Mietzins', wert: '', beschreibung: 'Höhe des monatlichen Mietzinses' },
    { kategorie: 'Nebenkosten', wert: 'Nach Verbrauch', beschreibung: 'Regelung der Nebenkosten' },
    { kategorie: 'Kaution', wert: '3 Monatsmieten', beschreibung: 'Höhe der Mietkaution' },
    { kategorie: 'Mietdauer', wert: 'Unbefristet', beschreibung: 'Befristung oder unbefristet' }
  ]
};

export default function VertraegeGenerator() {
  const { user } = useAuth();
  const [kontakte, setKontakte] = useState<Kontakt[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState<'generator' | 'historie'>('generator');
  const [vertragsHistory, setVertragsHistory] = useState<VertragsHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    contractData: any;
    htmlContent: string;
    printWindow: Window | null;
  }>({
    isOpen: false,
    contractData: null,
    htmlContent: '',
    printWindow: null
  });
  const [pendingContractId, setPendingContractId] = useState<string | null>(null);

  // Formular State
  const [formData, setFormData] = useState<VertragsData>({
    vertragsNummer: `V-${new Date().getFullYear()}-${String(Date.now()).slice(-3)}`,
    vertragsTyp: 'dienstleistung',
    kontaktPartei1: '',
    kontaktPartei2: '',
    vertragsDatum: new Date().toISOString().split('T')[0],
    startDatum: new Date().toISOString().split('T')[0],
    endDatum: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    betreff: '',
    beschreibung: '',
    bedingungen: standardBedingungen.dienstleistung.map((bed, index) => ({
      id: String(index + 1),
      kategorie: bed.kategorie,
      wert: bed.wert,
      beschreibung: bed.beschreibung
    })),
    zahlungskonditionen: 'Zahlbar innerhalb von 30 Tagen nach Rechnungsstellung.',
    kuendigungsklausel: 'Dieser Vertrag kann von beiden Parteien mit einer Frist von 3 Monaten zum Monatsende gekündigt werden.',
    firmenName: 'Dä Snäckomat Plüss',
    firmenAdresse: 'Eichliweg 33\\n5103 Möriken AG\\nSchweiz',
    firmenTelefon: '+41 76 629 09 01',
    firmenEmail: 'info@snaeckomat.ch',
    rechtlicheHinweise: 'Für diesen Vertrag gilt schweizerisches Recht. Gerichtsstand ist Aarau.',
    unterschriftOrt: 'Möriken AG'
  });

  // Load Contract History
  const loadContractHistory = async () => {
    if (!user) return;

    setHistoryLoading(true);
    try {
      const { data, error } = await supabase
        .from('contracts_history')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading contract history:', error);
        return;
      }

      setVertragsHistory(data || []);
    } catch (error) {
      console.error('Error loading contract history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Get Next Available Contract Number
  const getNextContractNumber = async (): Promise<string> => {
    if (!user) return '';

    try {
      const currentYear = new Date().getFullYear();

      const { data, error } = await supabase
        .from('contracts_history')
        .select('contract_number')
        .eq('user_id', user.id)
        .like('contract_number', `V-${currentYear}-%`)
        .order('contract_number', { ascending: false });

      if (error) {
        console.error('Error getting contract numbers:', error);
        return `V-${currentYear}-001`;
      }

      const usedNumbers = (data || [])
        .map(item => {
          const match = item.contract_number.match(new RegExp(`V-${currentYear}-(\\d+)`));
          return match ? parseInt(match[1]) : 0;
        })
        .filter(num => num > 0)
        .sort((a, b) => a - b);

      let nextNumber = 1;
      for (const num of usedNumbers) {
        if (num === nextNumber) {
          nextNumber++;
        } else {
          break;
        }
      }

      return `V-${currentYear}-${String(nextNumber).padStart(3, '0')}`;
    } catch (error) {
      console.error('Error generating contract number:', error);
      return `V-${new Date().getFullYear()}-001`;
    }
  };

  // Save Contract to History
  const saveContractToHistory = async (contractData: any): Promise<string | null> => {
    if (!user) return null;

    try {
      const historyItem = {
        user_id: user.id,
        contract_number: contractData.vertragsNummer,
        contract_type: contractData.vertragsTyp,
        party1_name: contractData.kontaktPartei1 || 'Unbekannte Partei 1',
        party2_name: contractData.kontaktPartei2 || 'Unbekannte Partei 2',
        subject: contractData.betreff || 'Kein Betreff',
        contract_date: contractData.vertragsDatum,
        start_date: contractData.startDatum,
        end_date: contractData.endDatum,
        status: 'entwurf' as const,
        is_sent: false,
        pdf_generated_count: 1,
        notes: contractData.beschreibung || null
      };

      const { data, error } = await supabase
        .from('contracts_history')
        .insert([historyItem])
        .select()
        .single();

      if (error) {
        console.error('Error saving contract to history:', error);
        return null;
      }

      setVertragsHistory(prev => [data, ...prev]);
      return data.id;
    } catch (error) {
      console.error('Error saving contract to history:', error);
      return null;
    }
  };

  // Update Contract Status
  const updateContractStatus = async (contractId: string, isSent: boolean) => {
    if (!user) return;

    try {
      const status = isSent ? 'versendet' : 'entwurf';
      const { error } = await supabase
        .from('contracts_history')
        .update({
          status,
          is_sent: isSent
        })
        .eq('id', contractId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error updating contract status:', error);
        return;
      }

      setVertragsHistory(prev =>
        prev.map(item =>
          item.id === contractId ? { ...item, status, is_sent: isSent } : item
        )
      );

      if (isSent) {
        const nextNumber = await getNextContractNumber();
        setFormData(prev => ({ ...prev, vertragsNummer: nextNumber }));
      }
    } catch (error) {
      console.error('Error updating contract status:', error);
    }
  };

  // Handle Confirmation Modal
  const handleConfirmationResponse = async (isSent: boolean) => {
    if (!pendingContractId) return;

    await updateContractStatus(pendingContractId, isSent);

    if (!isSent && confirmationModal.printWindow) {
      setTimeout(() => {
        if (confirmationModal.printWindow && !confirmationModal.printWindow.closed) {
          confirmationModal.printWindow.focus();
          confirmationModal.printWindow.print();
        }
      }, 500);
    }

    setConfirmationModal({
      isOpen: false,
      contractData: null,
      htmlContent: '',
      printWindow: null
    });
    setPendingContractId(null);

    await loadContractHistory();
  };

  // Delete Contract from History
  const deleteContractFromHistory = async (contractId: string, contractNumber: string) => {
    if (!user) return;

    const confirmDelete = window.confirm(
      `⚠️ Vertrag wirklich löschen?\\n\\n` +
        `Nummer: ${contractNumber}\\n\\n` +
        `ACHTUNG: Diese Aktion kann nicht rückgängig gemacht werden!`
    );

    if (!confirmDelete) return;

    try {
      const { error } = await supabase
        .from('contracts_history')
        .delete()
        .eq('id', contractId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error deleting contract:', error);
        alert('❌ Fehler beim Löschen des Vertrags');
        return;
      }

      setVertragsHistory(prev => prev.filter(item => item.id !== contractId));
      alert('✅ Vertrag wurde erfolgreich gelöscht');
    } catch (error) {
      console.error('Error deleting contract:', error);
      alert('❌ Fehler beim Löschen des Vertrags');
    }
  };

  // Load contacts
  const loadContacts = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user.id)
        .eq('data_type', 'contact')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading contacts:', error);
        return;
      }

      const contactData = (data ?? []).map(item => ({
        id: item.id,
        name: (item as any).data.name,
        firma: (item as any).data.firma,
        email: (item as any).data.email,
        telefon: (item as any).data.telefon,
        adresse: (item as any).data.adresse,
        plz: (item as any).data.plz,
        ort: (item as any).data.ort
      }));

      setKontakte(contactData);
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      Promise.all([loadContacts(), loadContractHistory()]);
    }
  }, [user]);

  // Update contract numbers when component loads
  useEffect(() => {
    if (user) {
      getNextContractNumber().then(nextNumber => {
        setFormData(prev => ({ ...prev, vertragsNummer: nextNumber }));
      });
    }
  }, [user]);

  // Handle contract type change
  const handleVertragsTypChange = (typ: string) => {
    const standardBeds = standardBedingungen[typ as keyof typeof standardBedingungen] || [];
    
    setFormData(prev => ({
      ...prev,
      vertragsTyp: typ,
      bedingungen: standardBeds.map((bed, index) => ({
        id: String(index + 1),
        kategorie: bed.kategorie,
        wert: bed.wert,
        beschreibung: bed.beschreibung
      }))
    }));
  };

  // Add condition
  const addBedingung = () => {
    const newBedingung: VertragsBedingung = {
      id: String(Date.now()),
      kategorie: '',
      wert: '',
      beschreibung: ''
    };
    setFormData(prev => ({
      ...prev,
      bedingungen: [...prev.bedingungen, newBedingung]
    }));
  };

  // Remove condition
  const removeBedingung = (id: string) => {
    setFormData(prev => ({
      ...prev,
      bedingungen: prev.bedingungen.filter(bed => bed.id !== id)
    }));
  };

  // Update condition
  const updateBedingung = (id: string, field: keyof VertragsBedingung, value: string) => {
    setFormData(prev => ({
      ...prev,
      bedingungen: prev.bedingungen.map(bed => 
        bed.id === id ? { ...bed, [field]: value } : bed
      )
    }));
  };

  // Generate PDF
  const generatePDF = async () => {
    if (!formData.betreff.trim()) {
      alert('Bitte geben Sie einen Betreff ein.');
      return;
    }

    if (formData.bedingungen.every(bed => !bed.kategorie.trim() || !bed.wert.trim())) {
      alert('Bitte füllen Sie mindestens eine Vertragsbedingung aus.');
      return;
    }

    setGenerating(true);

    try {
      const existingContract = vertragsHistory.find(
        item => item.contract_number === formData.vertragsNummer
      );

      let contractId: string | null = null;

      if (existingContract) {
        contractId = existingContract.id;
        await supabase
          .from('contracts_history')
          .update({
            pdf_generated_count: supabase.raw('pdf_generated_count + 1')
          })
          .eq('id', contractId)
          .eq('user_id', user.id);
      } else {
        contractId = await saveContractToHistory(formData);
      }

      const selectedKontakt1 = kontakte.find(k => k.name === formData.kontaktPartei1);
      const selectedKontakt2 = kontakte.find(k => k.name === formData.kontaktPartei2);

      // Generate HTML content
      const htmlContent = `
        <!DOCTYPE html>
        <html lang="de">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Vertrag ${formData.vertragsNummer}</title>
          <style>
            @page {
              size: A4;
              margin: 20mm;
            }
            
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            html, body {
              width: 210mm;
              font-family: 'Arial', sans-serif;
              font-size: 11pt;
              line-height: 1.4;
              color: #000;
              background: white;
            }
            
            .document {
              width: 170mm;
              margin: 0 auto;
              background: white;
            }
            
            .header {
              text-align: center;
              padding: 20px 0;
              border-bottom: 2px solid #333;
              margin-bottom: 30px;
            }
            
            .document-title {
              font-size: 24pt;
              font-weight: bold;
              margin-bottom: 10px;
              color: #333;
            }
            
            .contract-number {
              font-size: 14pt;
              color: #666;
              margin-bottom: 5px;
            }
            
            .contract-type {
              font-size: 12pt;
              color: #888;
              text-transform: uppercase;
              letter-spacing: 1px;
            }
            
            .parties-section {
              margin: 30px 0;
              display: flex;
              justify-content: space-between;
            }
            
            .party {
              width: 48%;
              padding: 15px;
              border: 1px solid #ddd;
              border-radius: 8px;
              background: #f9f9f9;
            }
            
            .party-title {
              font-size: 12pt;
              font-weight: bold;
              margin-bottom: 10px;
              color: #333;
              border-bottom: 1px solid #ccc;
              padding-bottom: 5px;
            }
            
            .party-info {
              font-size: 10pt;
              line-height: 1.5;
              color: #555;
            }
            
            .contract-details {
              margin: 30px 0;
              padding: 20px;
              background: #f5f5f5;
              border-radius: 8px;
            }
            
            .detail-row {
              display: flex;
              margin-bottom: 10px;
              font-size: 10pt;
            }
            
            .detail-label {
              font-weight: bold;
              width: 120px;
              color: #333;
            }
            
            .detail-value {
              flex: 1;
              color: #555;
            }
            
            .subject-section {
              margin: 30px 0;
            }
            
            .section-title {
              font-size: 14pt;
              font-weight: bold;
              margin-bottom: 15px;
              color: #333;
              border-bottom: 1px solid #333;
              padding-bottom: 5px;
            }
            
            .subject-content {
              font-size: 11pt;
              line-height: 1.6;
              color: #444;
              text-align: justify;
            }
            
            .conditions-table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
              font-size: 10pt;
            }
            
            .conditions-table th {
              background: #333;
              color: white;
              padding: 12px 8px;
              text-align: left;
              font-weight: bold;
            }
            
            .conditions-table td {
              padding: 10px 8px;
              border-bottom: 1px solid #ddd;
              vertical-align: top;
            }
            
            .conditions-table tr:nth-child(even) {
              background: #f9f9f9;
            }
            
            .conditions-table td:first-child {
              font-weight: bold;
              color: #333;
              width: 25%;
            }
            
            .legal-section {
              margin: 30px 0;
              padding: 15px;
              background: #fff;
              border: 1px solid #ddd;
              border-radius: 8px;
            }
            
            .signatures-section {
              margin: 40px 0;
              display: flex;
              justify-content: space-between;
            }
            
            .signature-box {
              width: 45%;
              text-align: center;
            }
            
            .signature-line {
              border-bottom: 1px solid #333;
              height: 50px;
              margin-bottom: 10px;
            }
            
            .signature-label {
              font-size: 10pt;
              color: #666;
            }
            
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #ddd;
              text-align: center;
              font-size: 9pt;
              color: #888;
            }
            
            @media print {
              body {
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
                print-color-adjust: exact;
              }
              
              .no-print {
                display: none !important;
              }
            }
            
            .download-btn {
              position: fixed;
              top: 20px;
              right: 20px;
              background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
              color: white;
              border: none;
              padding: 12px 24px;
              border-radius: 25px;
              font-size: 12pt;
              font-weight: bold;
              cursor: pointer;
              z-index: 1000;
              box-shadow: 0 4px 15px rgba(0,0,0,0.2);
            }
            
            .download-btn:hover {
              background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
              transform: translateY(-2px);
              box-shadow: 0 6px 20px rgba(0,0,0,0.3);
            }
          </style>
          <script>
            function downloadPDF() {
              window.print();
            }
            
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() {
                  window.close();
                }, 2000);
              }, 1000);
            };
          </script>
        </head>
        <body>
          <button class="download-btn no-print" onclick="downloadPDF()">
            PDF Speichern
          </button>

          <div class="document">
            <!-- HEADER -->
            <div class="header">
              <div class="document-title">VERTRAG</div>
              <div class="contract-number">Nr: ${formData.vertragsNummer}</div>
              <div class="contract-type">${vertragsTypen.find(t => t.value === formData.vertragsTyp)?.label || formData.vertragsTyp}</div>
            </div>

            <!-- VERTRAGSPARTEIEN -->
            <div class="parties-section">
              <div class="party">
                <div class="party-title">Partei 1 (Auftragnehmer)</div>
                <div class="party-info">
                  <strong>${formData.firmenName}</strong><br>
                  ${formData.firmenAdresse.replace(/\\n/g, '<br>')}<br>
                  Tel: ${formData.firmenTelefon}<br>
                  Email: ${formData.firmenEmail}
                </div>
              </div>
              <div class="party">
                <div class="party-title">Partei 2 (Auftraggeber)</div>
                <div class="party-info">
                  ${formData.kontaktPartei2 ? `
                    <strong>${selectedKontakt2?.name || formData.kontaktPartei2}</strong><br>
                    ${selectedKontakt2?.firma ? selectedKontakt2.firma + '<br>' : ''}
                    ${selectedKontakt2?.adresse ? selectedKontakt2.adresse + '<br>' : ''}
                    ${selectedKontakt2?.plz || ''} ${selectedKontakt2?.ort || ''}<br>
                    ${selectedKontakt2?.email ? 'Email: ' + selectedKontakt2.email + '<br>' : ''}
                    ${selectedKontakt2?.telefon ? 'Tel: ' + selectedKontakt2.telefon : ''}
                  ` : `
                    <strong>Auftraggeber</strong><br>
                    Details werden nachgetragen
                  `}
                </div>
              </div>
            </div>

            <!-- VERTRAGSDETAILS -->
            <div class="contract-details">
              <div class="detail-row">
                <div class="detail-label">Vertragsdatum:</div>
                <div class="detail-value">${new Date(formData.vertragsDatum).toLocaleDateString('de-CH')}</div>
              </div>
              <div class="detail-row">
                <div class="detail-label">Vertragsbeginn:</div>
                <div class="detail-value">${new Date(formData.startDatum).toLocaleDateString('de-CH')}</div>
              </div>
              <div class="detail-row">
                <div class="detail-label">Vertragsende:</div>
                <div class="detail-value">${new Date(formData.endDatum).toLocaleDateString('de-CH')}</div>
              </div>
              <div class="detail-row">
                <div class="detail-label">Unterschriftsort:</div>
                <div class="detail-value">${formData.unterschriftOrt}</div>
              </div>
            </div>

            <!-- BETREFF & BESCHREIBUNG -->
            <div class="subject-section">
              <div class="section-title">Vertragsgegenstand</div>
              <div class="subject-content">
                <strong>${formData.betreff}</strong><br><br>
                ${formData.beschreibung.replace(/\\n/g, '<br>') || 'Detaillierte Beschreibung des Vertragsgegenstands.'}
              </div>
            </div>

            <!-- VERTRAGSBEDINGUNGEN -->
            <div class="subject-section">
              <div class="section-title">Vertragsbedingungen</div>
              <table class="conditions-table">
                <thead>
                  <tr>
                    <th>Kategorie</th>
                    <th>Bedingung/Wert</th>
                    <th>Beschreibung</th>
                  </tr>
                </thead>
                <tbody>
                  ${formData.bedingungen
                    .filter(bed => bed.kategorie.trim() && bed.wert.trim())
                    .map(bedingung => `
                      <tr>
                        <td>${bedingung.kategorie}</td>
                        <td><strong>${bedingung.wert}</strong></td>
                        <td>${bedingung.beschreibung || '-'}</td>
                      </tr>
                    `).join('')}
                </tbody>
              </table>
            </div>

            <!-- ZAHLUNGSKONDITIONEN -->
            <div class="subject-section">
              <div class="section-title">Zahlungskonditionen</div>
              <div class="subject-content">
                ${formData.zahlungskonditionen}
              </div>
            </div>

            <!-- KÜNDIGUNG -->
            <div class="subject-section">
              <div class="section-title">Kündigung</div>
              <div class="subject-content">
                ${formData.kuendigungsklausel}
              </div>
            </div>

            <!-- RECHTLICHE HINWEISE -->
            <div class="legal-section">
              <div class="section-title">Rechtliche Hinweise</div>
              <div class="subject-content">
                ${formData.rechtlicheHinweise}
              </div>
            </div>

            <!-- UNTERSCHRIFTEN -->
            <div class="signatures-section">
              <div class="signature-box">
                <div class="signature-line"></div>
                <div class="signature-label">
                  Datum, Unterschrift<br>
                  <strong>${formData.firmenName}</strong>
                </div>
              </div>
              <div class="signature-box">
                <div class="signature-line"></div>
                <div class="signature-label">
                  Datum, Unterschrift<br>
                  <strong>${formData.kontaktPartei2 || 'Auftraggeber'}</strong>
                </div>
              </div>
            </div>

            <!-- FOOTER -->
            <div class="footer">
              <p>Vertrag erstellt am ${new Date().toLocaleDateString('de-CH')} • ${formData.firmenName} • ${formData.firmenEmail}</p>
            </div>
          </div>
        </body>
        </html>
      `;

      // Download HTML file
      const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);

      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = `Vertrag_${formData.vertragsNummer}_${new Date().toISOString().split('T')[0]}.html`;
      downloadLink.style.display = 'none';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      // Open print window
      const printWindow = window.open('', '_blank', 'width=794,height=1123,scrollbars=no,resizable=no');
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        
        printWindow.onload = function() {
          setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            setTimeout(() => {
              printWindow.close();
            }, 1000);
          }, 1000);
        };
      }

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 30000);

      // Status confirmation modal
      if (contractId) {
        setPendingContractId(contractId);
        setConfirmationModal({
          isOpen: true,
          contractData: formData,
          htmlContent,
          printWindow: printWindow
        });
      }

      if (!existingContract) {
        alert(`✅ Vertrag wurde erfolgreich generiert!\\n\\n📄 HTML-Datei heruntergeladen\\n💾 In Historie gespeichert\\n\\n📊 Bitte bestätigen Sie den Status nach dem Drucken.`);
      }
    } catch (error) {
      console.error('Error generating contract:', error);
      alert('❌ Fehler beim Generieren des Vertrags');
    } finally {
      setGenerating(false);
    }
  };

  // Kontakt options
  const kontaktOptionen = kontakte.map(kontakt => ({
    value: kontakt.name,
    label: kontakt.firma ? `${kontakt.name} (${kontakt.firma})` : kontakt.name
  }));

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Lade Vertrags-Generator...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-200">
        <div className="w-full px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <i className="ri-contract-line text-purple-600 text-lg"></i>
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Vertrags-Generator</h1>
                <p className="text-sm text-gray-600 mt-1">
                  Professionelle Verträge mit intelligenter Nummernverwaltung
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2 px-3 py-1.5 bg-purple-100 rounded-lg">
              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              <span className="text-sm font-medium text-purple-700">🇨🇭 Schweizer Recht</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <div className="w-full px-3 sm:px-6">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('generator')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'generator'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-contract-line mr-2"></i>
              Vertrags-Generator
            </button>
            <button
              onClick={() => setActiveTab('historie')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'historie'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-history-line mr-2"></i>
              Vertragshistorie
              {vertragsHistory.filter(item => item.status === 'entwurf' && !item.is_sent).length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                  {vertragsHistory.filter(item => item.status === 'entwurf' && !item.is_sent).length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="w-full px-3 sm:px-6 py-4 sm:py-6">
        {activeTab === 'historie' ? (
          // Contract History Tab
          <div className="max-w-6xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                      <i className="ri-history-line mr-2 text-purple-600"></i>
                      Vertragshistorie & Status-Verfolgung
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Alle generierten Verträge und deren Lebenszyklus
                    </p>
                  </div>
                  <Button
                    onClick={loadContractHistory}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap"
                    disabled={historyLoading}
                  >
                    {historyLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Lade...
                      </>
                    ) : (
                      <>
                        <i className="ri-refresh-line mr-2"></i>
                        Aktualisieren
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {historyLoading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Lade Historie...</p>
                </div>
              ) : vertragsHistory.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-contract-line text-2xl text-gray-400"></i>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Verträge vorhanden</h3>
                  <p className="text-gray-600">
                    Generieren Sie Ihren ersten Vertrag, um die Historie zu sehen.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Nummer & Typ
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Vertragsparteien
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Laufzeit
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Aktionen
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {vertragsHistory.map(item => (
                        <tr
                          key={item.id}
                          className={`hover:bg-gray-50 ${
                            item.status === 'entwurf' && !item.is_sent ? 'bg-yellow-50' : ''
                          }`}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                                <i className="ri-contract-line text-purple-600 text-sm"></i>
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">{item.contract_number}</div>
                                <div className="text-xs text-gray-500 capitalize">
                                  {vertragsTypen.find(t => t.value === item.contract_type)?.label || item.contract_type}
                                  {item.pdf_generated_count > 1 && (
                                    <span className="ml-1 text-orange-600">
                                      ({item.pdf_generated_count}x generiert)
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                              {item.party1_name} ↔ {item.party2_name}
                            </div>
                            <div className="text-xs text-gray-500 truncate max-w-xs">{item.subject}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {new Date(item.start_date).toLocaleDateString('de-DE')}
                            </div>
                            <div className="text-xs text-gray-500">
                              bis {new Date(item.end_date).toLocaleDateString('de-DE')}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                item.status === 'entwurf' && !item.is_sent
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : item.status === 'versendet'
                                  ? 'bg-blue-100 text-blue-800'
                                  : item.status === 'unterzeichnet'
                                  ? 'bg-green-100 text-green-800'
                                  : item.status === 'aktiv'
                                  ? 'bg-green-100 text-green-800'
                                  : item.status === 'beendet'
                                  ? 'bg-gray-100 text-gray-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {item.status === 'entwurf' && !item.is_sent
                                ? '⏳ Entwurf'
                                : item.status === 'versendet'
                                ? '📤 Versendet'
                                : item.status === 'unterzeichnet'
                                ? '✍️ Unterzeichnet'
                                : item.status === 'aktiv'
                                ? '✅ Aktiv'
                                : item.status === 'beendet'
                                ? '🏁 Beendet'
                                : '❌ Gekündigt'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                            {item.status === 'entwurf' && !item.is_sent && (
                              <>
                                <button
                                  onClick={() => updateContractStatus(item.id, true)}
                                  className="text-blue-600 hover:text-blue-700 cursor-pointer px-2 py-1 rounded hover:bg-blue-50"
                                  title="Als versendet markieren"
                                >
                                  📤 Versendet
                                </button>
                                <button
                                  onClick={() => {
                                    setFormData(prev => ({ ...prev, vertragsNummer: item.contract_number }));
                                    setActiveTab('generator');
                                  }}
                                  className="text-green-600 hover:text-green-700 cursor-pointer px-2 py-1 rounded hover:bg-green-50"
                                  title="Nummer wiederverwenden"
                                >
                                  🔄 Wiederverwenden
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => deleteContractFromHistory(item.id, item.contract_number)}
                              className="text-red-600 hover:text-red-700 cursor-pointer px-2 py-1 rounded hover:bg-red-50"
                              title="Vertrag löschen"
                            >
                              🗑️ Löschen
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Statistics */}
              {vertragsHistory.length > 0 && (
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900">{vertragsHistory.length}</div>
                      <div className="text-xs text-gray-500">Gesamt Verträge</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-yellow-600">
                        {vertragsHistory.filter(item => item.status === 'entwurf').length}
                      </div>
                      <div className="text-xs text-gray-500">Entwürfe</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {vertragsHistory.filter(item => item.status === 'versendet').length}
                      </div>
                      <div className="text-xs text-gray-500">Versendet</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {vertragsHistory.filter(item => item.status === 'aktiv' || item.status === 'unterzeichnet').length}
                      </div>
                      <div className="text-xs text-gray-500">Aktiv</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-600">
                        {vertragsHistory.filter(item => item.status === 'beendet').length}
                      </div>
                      <div className="text-xs text-gray-500">Beendet</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          // Generator Form
          <div className="max-w-4xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <form className="space-y-6">
                {/* Firmeninformationen */}
                <div className="bg-purple-50 border-purple-200 p-4 rounded-lg border">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <i className="ri-building-line mr-2"></i>
                    Ihre Firmeninformationen (Partei 1)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Firmenname"
                      value={formData.firmenName}
                      onChange={e => setFormData(prev => ({ ...prev, firmenName: e.target.value }))}
                      placeholder="Dä Snäckomat Plüss"
                    />
                    <Input
                      label="Telefon"
                      value={formData.firmenTelefon}
                      onChange={e => setFormData(prev => ({ ...prev, firmenTelefon: e.target.value }))}
                      placeholder="+41 76 629 09 01"
                    />
                    <Input
                      label="Email"
                      type="email"
                      value={formData.firmenEmail}
                      onChange={e => setFormData(prev => ({ ...prev, firmenEmail: e.target.value }))}
                      placeholder="info@snaeckomat.ch"
                    />
                    <Input
                      label="Unterschriftsort"
                      value={formData.unterschriftOrt}
                      onChange={e => setFormData(prev => ({ ...prev, unterschriftOrt: e.target.value }))}
                      placeholder="Möriken AG"
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Firmenadresse</label>
                    <textarea
                      value={formData.firmenAdresse}
                      onChange={e => setFormData(prev => ({ ...prev, firmenAdresse: e.target.value }))}
                      placeholder="Eichliweg 33&#10;5103 Möriken AG&#10;Schweiz"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm resize-none"
                    />
                  </div>
                </div>

                {/* Grunddaten */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Vertragsnummer"
                    value={formData.vertragsNummer}
                    onChange={e => setFormData(prev => ({ ...prev, vertragsNummer: e.target.value }))}
                    placeholder="V-2025-001"
                  />

                  <Select
                    label="Vertragstyp"
                    options={vertragsTypen}
                    value={formData.vertragsTyp}
                    onChange={value => handleVertragsTypChange(value as string)}
                  />

                  <Select
                    label="Partei 2 (Vertragspartner)"
                    options={[
                      { value: '', label: 'Vertragspartner auswählen...' },
                      ...kontaktOptionen
                    ]}
                    value={formData.kontaktPartei2}
                    onChange={value => setFormData(prev => ({ ...prev, kontaktPartei2: value as string }))}
                    placeholder="Vertragspartner auswählen..."
                  />

                  <Input
                    label="Vertragsdatum"
                    type="date"
                    value={formData.vertragsDatum}
                    onChange={e => setFormData(prev => ({ ...prev, vertragsDatum: e.target.value }))}
                  />

                  <Input
                    label="Vertragsbeginn"
                    type="date"
                    value={formData.startDatum}
                    onChange={e => setFormData(prev => ({ ...prev, startDatum: e.target.value }))}
                  />

                  <Input
                    label="Vertragsende"
                    type="date"
                    value={formData.endDatum}
                    onChange={e => setFormData(prev => ({ ...prev, endDatum: e.target.value }))}
                  />
                </div>

                {/* Betreff & Beschreibung */}
                <div className="space-y-4">
                  <Input
                    label="Vertragsgegenstand/Betreff"
                    value={formData.betreff}
                    onChange={e => setFormData(prev => ({ ...prev, betreff: e.target.value }))}
                    placeholder="z.B. Wartungsvertrag für Automaten, Dienstleistungsvertrag..."
                    required
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Detaillierte Beschreibung
                    </label>
                    <textarea
                      value={formData.beschreibung}
                      onChange={e => setFormData(prev => ({ ...prev, beschreibung: e.target.value }))}
                      placeholder="Detaillierte Beschreibung des Vertragsgegenstands, Leistungen, Pflichten..."
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm resize-none"
                    />
                  </div>
                </div>

                {/* Vertragsbedingungen */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                      <i className="ri-list-check mr-2 text-purple-600"></i>
                      Vertragsbedingungen
                    </h3>
                    <Button
                      type="button"
                      onClick={addBedingung}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap"
                    >
                      <i className="ri-add-line mr-2"></i>
                      Bedingung hinzufügen
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {formData.bedingungen.map((bedingung, index) => (
                      <div key={bedingung.id} className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-900 flex items-center">
                            <i className="ri-checkbox-line mr-2 text-purple-600"></i>
                            Bedingung {index + 1}
                          </h4>
                          <button
                            type="button"
                            onClick={() => removeBedingung(bedingung.id)}
                            className="text-red-600 hover:text-red-700 p-1"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <Input
                            label="Kategorie"
                            value={bedingung.kategorie}
                            onChange={e => updateBedingung(bedingung.id, 'kategorie', e.target.value)}
                            placeholder="z.B. Laufzeit, Vergütung, Kündigung..."
                          />
                          <Input
                            label="Bedingung/Wert"
                            value={bedingung.wert}
                            onChange={e => updateBedingung(bedingung.id, 'wert', e.target.value)}
                            placeholder="z.B. 12 Monate, CHF 500, 3 Monate..."
                          />
                          <div className="md:col-span-1">
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                              Beschreibung (optional)
                            </label>
                            <textarea
                              value={bedingung.beschreibung || ''}
                              onChange={e => updateBedingung(bedingung.id, 'beschreibung', e.target.value)}
                              placeholder="Zusätzliche Details..."
                              rows={2}
                              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-xs sm:text-sm resize-none"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {formData.bedingungen.length === 0 && (
                    <div className="text-center py-8 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="text-purple-400 mb-3">
                        <i className="ri-list-check text-4xl"></i>
                      </div>
                      <h4 className="text-lg font-medium text-gray-700 mb-2">Keine Bedingungen hinzugefügt</h4>
                      <p className="text-gray-500 text-sm">
                        Fügen Sie strukturierte Vertragsbedingungen hinzu.
                      </p>
                    </div>
                  )}
                </div>

                {/* Zahlungskonditionen & Kündigung */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Zahlungskonditionen
                    </label>
                    <textarea
                      value={formData.zahlungskonditionen}
                      onChange={e => setFormData(prev => ({ ...prev, zahlungskonditionen: e.target.value }))}
                      placeholder="Zahlungskonditionen und -modalitäten..."
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Kündigungsklausel
                    </label>
                    <textarea
                      value={formData.kuendigungsklausel}
                      onChange={e => setFormData(prev => ({ ...prev, kuendigungsklausel: e.target.value }))}
                      placeholder="Kündigungsfristen und -modalitäten..."
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rechtliche Hinweise
                    </label>
                    <textarea
                      value={formData.rechtlicheHinweise}
                      onChange={e => setFormData(prev => ({ ...prev, rechtlicheHinweise: e.target.value }))}
                      placeholder="Anwendbares Recht, Gerichtsstand..."
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm resize-none"
                    />
                  </div>
                </div>

                {/* Zusammenfassung */}
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                    <i className="ri-file-text-line mr-2 text-purple-600"></i>
                    Vertragsübersicht
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Vertragstyp:</span>
                      <span className="font-medium">{vertragsTypen.find(t => t.value === formData.vertragsTyp)?.label}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Laufzeit:</span>
                      <span className="font-medium">
                        {new Date(formData.startDatum).toLocaleDateString('de-CH')} - {new Date(formData.endDatum).toLocaleDateString('de-CH')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Bedingungen:</span>
                      <span className="font-medium">
                        {formData.bedingungen.filter(b => b.kategorie.trim() && b.wert.trim()).length} definiert
                      </span>
                    </div>
                  </div>
                </div>

                {/* Aktionen */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button
                    type="button"
                    onClick={generatePDF}
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium whitespace-nowrap"
                    disabled={generating || !formData.betreff.trim() || formData.bedingungen.every(bed => !bed.kategorie.trim() || !bed.wert.trim())}
                  >
                    {generating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Generiere Vertrag...
                      </>
                    ) : (
                      <>
                        <i className="ri-contract-line mr-2"></i>
                        Vertrag als PDF herunterladen
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Status Confirmation Modal */}
      <Modal
        isOpen={confirmationModal.isOpen}
        onClose={() => {}}
        title="📋 Vertrags-Status bestätigen"
        maxWidth="md"
      >
        <div className="space-y-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-contract-line text-2xl text-purple-600"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Wurde der Vertrag wirklich versendet?
            </h3>
            <p className="text-gray-600 text-sm">
              Diese Bestätigung hilft bei der korrekten Vertragsverwaltung
            </p>
          </div>

          {confirmationModal.contractData && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">Vertrags-Details:</h4>
              <div className="text-sm space-y-1">
                <div>
                  <strong>Nummer:</strong> {confirmationModal.contractData.vertragsNummer}
                </div>
                <div>
                  <strong>Typ:</strong> {vertragsTypen.find(t => t.value === confirmationModal.contractData.vertragsTyp)?.label}
                </div>
                <div>
                  <strong>Partner:</strong> {confirmationModal.contractData.kontaktPartei2 || 'Kein Partner'}
                </div>
                <div>
                  <strong>Betreff:</strong> {confirmationModal.contractData.betreff}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex">
                <i className="ri-mail-send-line text-blue-600 mr-2 mt-0.5"></i>
                <div>
                  <h5 className="font-medium text-blue-900">JA - Vertrag wurde versendet</h5>
                  <p className="text-sm text-blue-700 mt-1">
                    • Status wird auf "versendet" gesetzt<br />
                    • Nächste Vertragsnummer wird generiert<br />
                    • Vertrag wird in Historie getrackt
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <div className="flex">
                <i className="ri-draft-line text-orange-600 mr-2 mt-0.5"></i>
                <div>
                  <h5 className="font-medium text-orange-900">NEIN - Vertrag nicht versendet</h5>
                  <p className="text-sm text-orange-700 mt-1">
                    • Status bleibt auf "Entwurf"<br />
                    • Nummer kann wiederverwendet werden<br />
                    • PDF-Dialog öffnet sich erneut
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <Button
              onClick={() => handleConfirmationResponse(false)}
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-medium"
            >
              <i className="ri-draft-line mr-2"></i>
              NEIN - Entwurf
            </Button>
            <Button
              onClick={() => handleConfirmationResponse(true)}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium"
            >
              <i className="ri-mail-send-line mr-2"></i>
              JA - Versendet
            </Button>
          </div>

          <div className="text-center">
            <p className="text-xs text-gray-500">
              💡 Diese Bestätigung hilft bei der Vertragsverwaltung und -verfolgung
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
}
